import { DOMESTIC_NUTRITION_KEYS } from "../../Utils/constants.js"
import {
  LabelJson,
  BaseLabelJSON,
  InterleavedOtherNutrient,
} from "../../constants/exportsCountries.js"
interface PhilippinesMandatoryNutrients {
  ENERGY: string
  TOTAL_CARBOHYDRATE: string
  TOTAL_SUGARS: string
  PROTEIN: string
  TOTAL_FAT: string
  SATURATED_FAT: string
  SODIUM: string
  CHOLESTEROL: string
  TRANS_FAT: string
  DIETARY_FIBRE: string
}

export const PHILIPPINES_MANDATORY_NUTRIENTS: PhilippinesMandatoryNutrients = {
  ENERGY: DOMESTIC_NUTRITION_KEYS.ENERGY,
  TOTAL_CARBOHYDRATE: DOMESTIC_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
  TOTAL_SUGARS: DOMESTIC_NUTRITION_KEYS.TOTAL_SUGARS,
  PROTEIN: DOMESTIC_NUTRITION_KEYS.PROTEIN,
  TOTAL_FAT: DOMESTIC_NUTRITION_KEYS.TOTAL_FAT,
  SATURATED_FAT: DOMESTIC_NUTRITION_KEYS.SATURATED_FAT,
  SODIUM: DOMESTIC_NUTRITION_KEYS.SODIUM,
  CHOLESTEROL: DOMESTIC_NUTRITION_KEYS.CHOLESTEROL,
  TRANS_FAT: DOMESTIC_NUTRITION_KEYS.TRANS_FAT,
  DIETARY_FIBRE: DOMESTIC_NUTRITION_KEYS.DIETARY_FIBRE,
}

export const PHILIPPINES_NON_MANDATORY_NUTRIENTS = [
  {
    key: DOMESTIC_NUTRITION_KEYS.ADDED_SUGARS,
    value: "Added Sugars (g)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.ORYZANOL,
    value: "Oryzanol (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.IRON,
    value: "Iron (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.CALCIUM,
    value: "Calcium (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.FOLIC_ACID,
    value: "Folic Acid (mcg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.IODINE,
    value: "Iodine (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_B6,
    value: "Vitamin B6 (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_B12,
    value: "Vitamin B12 (mcg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_C,
    value: "Vitamin C (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.ZINC,
    value: "Zinc (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.MAGNESIUM,
    value: "Magnesium (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_D,
    value: "Vitamin D (mcg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_K1,
    value: "Vitamin K1 (mcg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_B1,
    value: "Vitamin B1 (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_B2,
    value: "Vitamin B2 (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.BIOTIN,
    value: "Biotin (mcg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_B3,
    value: "Vitamin B3 (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.PANTOTHENIC_ACID,
    value: "Pantothenic Acid (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_E,
    value: "Vitamin E (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.VITAMIN_A,
    value: "Vitamin A (mcg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.SELENIUM,
    value: "Selenium (mcg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.COPPER,
    value: "Copper (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.POTASSIUM,
    value: "Potassium (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.CHLORIDE,
    value: "Chloride (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.MANGANESE,
    value: "Manganese (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.PHOSPHORUS,
    value: "Phosphorus (mg)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.MUFA,
    value: "MUFA (g)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.PUFA,
    value: "PUFA (g)",
  },
  {
    key: DOMESTIC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS,
    value: "Omega 3 Fatty Acids (g)",
  },

  {
    key: DOMESTIC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS,
    value: "Omega 6 Fatty Acids (g)",
  },
] as const

export interface BackOfPackPhilippinesRightProps {
  data: { label_json?: LabelJson }
  showPreview: boolean
  userAddress: any
}

export interface FrontOfPackPhilippinesProps {
  data: { label_json?: LabelJson }
  brandsMapping: Record<string, any>
}

export interface NutrientValue {
  key: string
  name: string
  per_serve: number | string // e.g. 10
  reni_value: number | string // e.g. 24, '-', etc.
}

export interface PhilippinesNiTableCalculatedValue {
  serving_size: number
  serving_size_unit: string
  country: string
  no_of_servings: number
  calories: number
  calories_from_fat: number
  calories_reni_value: number | string
  mandatory_nutrient_values: {
    ENERGY: NutrientValue
    TOTAL_CARBOHYDRATES: NutrientValue
    SUGAR: NutrientValue
    TOTAL_PROTEIN: NutrientValue
    TOTAL_FAT: NutrientValue
    SATURATED_FAT: NutrientValue
    SODIUM: NutrientValue
    CHOLESTEROL: NutrientValue
    TRANS_FAT: NutrientValue
    DIETARY_FIBER: NutrientValue
  }
  non_mandatory_nutrient_values: NutrientValue[]
  age_group?: keyof typeof DOMESTIC_TO_PHILIPPINES_AGE_GROUP_MAPPING
}

export interface PhilippinesNonMandatoryNutrient {
  name: (typeof PHILIPPINES_NON_MANDATORY_NUTRIENTS)[number]["key"]
  quantity: string
}

export interface PhilippinesLabelJson extends BaseLabelJSON {
  mandatory_nutrient: PhilippinesMandatoryNutrients[]
  copy_mandatory_nutrient: PhilippinesMandatoryNutrients[]
  non_mandatory_nutrient: PhilippinesNonMandatoryNutrient[]
  ni_table_calculated_values: PhilippinesNiTableCalculatedValue
}

export const DOMESTIC_TO_PHILIPPINES_AGE_GROUP_MAPPING = {
  "Above 18 years": "19-29 yr old",
  "1-3 years": "1-2 years",
  "4-6 years": "3-5 years",
  "7-9 years": "6-9 years",
  "10-12 years": "10-12 years",
  "13-15 years": "13-15 years",
  "16-18 years": "16-18 years",
  "0-6 months": "0-6 months",
  "7-12 months": "7-12 months",
}

export const PHILIPPINES_INTERLEAVED_NUTRIENTS: InterleavedOtherNutrient[] = [
  { name: "MUFA", key: DOMESTIC_NUTRITION_KEYS.MUFA },
  { name: "PUFA", key: DOMESTIC_NUTRITION_KEYS.PUFA },
  { name: "OMEGA_3", key: DOMESTIC_NUTRITION_KEYS.OMEGA_3_FATTY_ACIDS },
  { name: "OMEGA_6", key: DOMESTIC_NUTRITION_KEYS.OMEGA_6_FATTY_ACIDS },
  { name: "ADDED_SUGAR", key: DOMESTIC_NUTRITION_KEYS.ADDED_SUGARS },
]
